"use client";

import { type LiftSuitability } from "@/lib/planner/lift-suitability";
import type { PlannerItinerary } from "@/lib/planner/api-types";
import type { ItineraryLeg } from "@/lib/planner/itinerary";
import type { AreaSport } from "@/lib/planner/graph-types";
import type { LegRole } from "@/lib/planner/route-objective";
import { CLOSED_LIFTS_SUMMARY, CLOSED_LIFTS_GUIDANCE, visibleEmptyStateWarnings, type RouteUnavailableReason } from "@/lib/planner/empty-state";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";
import { ASPECT_LABEL, difficultyColour, difficultyLabel, difficultySymbol } from "./trail-colours";
import { RouteRoleBadge } from "./RouteRoleBadge";
import { DIFFICULTY_RANK } from "@/lib/planner/preferences";
import type { Difficulty } from "@/lib/planner/graph-types";
import { formatHm } from "@/lib/planner/format-time";
import { priceTierLabel } from "@/lib/planner/places-display";
import { placeEvidenceLabel, type PlaceSourceEvidence } from "@/lib/planner/venue-policy";

// Movement-leg icons, keyed by sport. The `climb` row is MTB-only (ski plans
// never produce a climb leg). Ski icons are byte-identical to before.
const LEG_ICON: Record<AreaSport, Record<"lift" | "trail" | "climb" | "link" | "bus", string>> = {
  ski: {
    lift: "🚡",
    trail: "🎿",
    climb: "🔼",
    link: "🥾",
    bus: "🚌",
  },
  mtb: {
    lift: "🚡",
    trail: "🚵",
    climb: "⛰️",
    link: "🚶",
    bus: "🚐",
  },
};

type StopDetail = PlaceSourceEvidence & {
  rating: number | null;
  priceLevel: number | null;
  cuisine: string | null;
  mapsUri: string | null;
  reservable: boolean | null;
};

type TimelineItem =
  | {
      kind: "descent";
      departHour: number;
      title: string;
      bestScore: number | null;
      aspect: string | null;
      tempC: number | null;
      groomed: boolean;
      difficulty: string | null;
      sourceGrade: string | null;
      reason: string | null;
      role: LegRole | undefined;
      appeal: number | undefined;
      appealSource: "curated" | "heuristic" | undefined;
      appealNote: string | null;
    }
  | {
      kind: "lift" | "link" | "bus" | "climb";
      direction?: "down";
      suitability?: LiftSuitability;
      departHour: number;
      title: string;
      reason?: string | null;
      role: LegRole | undefined;
    }
  | {
      kind: "stop";
      stopType: "coffee" | "lunch" | "apres";
      departHour: number;
      arriveHour: number;
      title: string;
      detail: StopDetail | null;
    };

const STOP_ICON: Record<"coffee" | "lunch" | "apres", string> = {
  coffee: "☕",
  lunch: "🍽️",
  apres: "🍻",
};

function priceText(level: number | null): string {
  return priceTierLabel(level) ?? "";
}

// A trail leg may carry an optional difficulty grade (additive; absent on
// legs the engine has not yet annotated). Read through this narrow widening so
// the chip lights up automatically once the grade is surfaced, with no `any`.
type GradedTrail = { difficulty?: string | null };

const ROLE_PRIORITY: LegRole[] = [
  "return-home",
  "primary-run",
  "good-run",
  "stop-detour",
  "weak-necessary",
  "connector",
  "uplift",
  "transfer",
];

function strongerRole(current: LegRole | undefined, next: LegRole | undefined): LegRole | undefined {
  if (!next) return current;
  if (!current) return next;
  return ROLE_PRIORITY.indexOf(next) < ROLE_PRIORITY.indexOf(current) ? next : current;
}

/** Group consecutive descent legs into a single "ride/ski down" descent. */
function groupItems(legs: ItineraryLeg[], sport: AreaSport): TimelineItem[] {
  const items: TimelineItem[] = [];
  let i = 0;
  while (i < legs.length) {
    const leg = legs[i];
    if (leg.kind === "stop") {
      items.push({
        kind: "stop",
        stopType: leg.stopType,
        departHour: leg.departHour,
        arriveHour: leg.arriveHour,
        title: leg.label,
        detail: leg.place
          ? {
              rating: leg.place.rating,
              priceLevel: leg.place.priceLevel,
              cuisine: leg.place.cuisine,
              mapsUri: leg.place.mapsUri,
              reservable: leg.place.reservable,
              source: leg.place.source, sourceUrl: leg.place.sourceUrl, sourceCheckedAt: leg.place.sourceCheckedAt,
              openingState: leg.place.openingState, accessMinutes: leg.place.accessMinutes, accessDistanceM: leg.place.accessDistanceM,
            }
          : null,
      });
      i += 1;
      continue;
    }
    // An MTB pedal-up leg is a `trail` movement with `climb: true`. It is its
    // own row (a climb, not a descent) and must NOT merge into an adjacent
    // descent. Ski legs never set `climb`, so this branch never fires for ski.
    if (leg.kind === "trail" && leg.climb === true) {
      items.push({
        kind: "climb",
        departHour: leg.departHour,
        title: leg.label && leg.label !== "Climb" ? `Climb to ${leg.label}` : "Climb",
        role: leg.role,
      });
      i += 1;
      continue;
    }
    if (leg.kind === "link") {
      const departHour = leg.departHour;
      const title = leg.label;
      let reason: string | null = null;
      let role: LegRole | undefined;
      while (i < legs.length) {
        const link = legs[i];
        if (link.kind !== "link") break;
        if (!reason && link.reason) reason = link.reason;
        role = strongerRole(role, link.role);
        i += 1;
      }
      items.push({ kind: "link", departHour, title, reason, role });
      continue;
    }
    if (leg.kind === "trail") {
      const names: string[] = [];
      let bestScore: number | null = null;
      let missingScore = false;
      let aspect: string | null = null;
      let tempC: number | null = null;
      let groomed = false;
      let difficulty: string | null = null;
      let sourceGrade: string | null = null;
      let reason: string | null = null;
      let role: LegRole | undefined;
      let appeal: number | undefined;
      let appealSource: "curated" | "heuristic" | undefined;
      let appealNote: string | null = null;
      const departHour = leg.departHour;
      while (i < legs.length) {
        const t = legs[i];
        // Stop the descent at a climb boundary: a pedal-up is a separate row.
        if (t.kind !== "trail" || t.climb === true) break;
        if (t.label && !names.includes(t.label)) names.push(t.label);
        if (t.trail?.score == null) missingScore = true;
        if (t.trail) {
          if (t.trail.score != null && (bestScore == null || t.trail.score < bestScore)) {
            bestScore = t.trail.score;
            aspect = t.trail.aspect;
            tempC = t.trail.tempC;

          }
          const grade = (t.trail as GradedTrail).difficulty;
          if (grade && (DIFFICULTY_RANK[grade as Difficulty] ?? -1) > (DIFFICULTY_RANK[difficulty as Difficulty] ?? -1)) { difficulty = grade; sourceGrade = t.trail.sourceGrade ?? null; }
          if (t.trail.freshlyGroomed) groomed = true;
        }
        // The descent's why-line: the first sub-leg with something to say.
        if (!reason && t.reason) reason = t.reason;
        if ((t.scoreBreakdown?.appeal ?? 0) > (appeal ?? 0)) {
          appeal = t.scoreBreakdown?.appeal;
          appealSource = t.scoreBreakdown?.appealSource;
          appealNote = t.scoreBreakdown?.appealNote ?? null;
        }
        role = strongerRole(role, t.role);
        i += 1;
      }
      if (missingScore) bestScore = null;
      const fallback = sport === "mtb" ? "Ride down" : "Ski down";
      const title =
        names.length === 0
          ? fallback
          : names.length <= 2
            ? names.join(", then ")
            : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
      items.push({ kind: "descent", departHour, title, bestScore, aspect, tempC, groomed, difficulty, sourceGrade, reason, role, appeal, appealSource, appealNote });
    } else {
      items.push({
        kind: leg.kind,
        direction: leg.lift?.direction,
        suitability: leg.lift?.suitability,
        departHour: leg.departHour,
        title: leg.label,
        reason: leg.reason ?? null,
        role: leg.role,
      });
      i += 1;
    }
  }
  return items;
}

type Props = {
  itinerary: PlannerItinerary;
  scoredDate: string;
  /** Which sport's icons/labels/copy to render. Defaults to ski (unchanged). */
  sport?: AreaSport;
  /** Ski area whose runs all score zero today: show a cause-neutral conditions
   * message on the empty state rather than the settings/ski-bus suggestion. */
  conditionsThin?: boolean;
  unavailableReason?: RouteUnavailableReason;
};

export function ItineraryTimeline({ itinerary, scoredDate, sport = "ski", conditionsThin = false, unavailableReason }: Props) {
  const { legs, totals, warnings, weatherTimeline } = itinerary;
  const weatherByHour = new Map(weatherTimeline.map((w) => [w.hour, w]));
  const isMtb = sport === "mtb";

  if (legs.length === 0) {
    // Calm, single empty state. The map alongside still shows every run we have
    // for the area, so this is a "here is what we have" moment, not an error.
    // Internal engine warnings that duplicate this message or describe a
    // non-existent plan are filtered out; a genuine one (an editor reject) shows.
    const liftsClosed = unavailableReason === "lifts-closed";
    const shownWarnings = conditionsThin ? [] : visibleEmptyStateWarnings(warnings).filter((warning) => !liftsClosed || warning !== `${CLOSED_LIFTS_SUMMARY} ${CLOSED_LIFTS_GUIDANCE}`);
    return (
      <div className="rounded-[16px] border border-border-light bg-warm-bg p-5">
        <p className="text-[14px] font-semibold text-navy">
          {liftsClosed ? CLOSED_LIFTS_SUMMARY : conditionsThin
            ? "No suitable ski route in the current forecast"
            : isMtb
              ? "We could not map a full ride here today"
              : "We could not map a full day here today"}
        </p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-text-muted">
          {liftsClosed ? CLOSED_LIFTS_GUIDANCE : conditionsThin
            ? "Current forecast conditions do not support a suitable ski route. Check the operator's latest mountain report before setting off."
            : isMtb
              ? "The map alongside shows every trail we have for this area. The riding network we can route across here is still limited. Try a later finish, a different start point, or allowing shuttle links."
              : "The map alongside shows every run we have for this area. Try a later finish, a different start point, or allowing the ski bus."}
        </p>
        {shownWarnings.map((w, i) => (
          <p key={i} className="mt-2 text-[12.5px] text-text-muted">
            {w}
          </p>
        ))}
      </div>
    );
  }

  const dateLabel = new Date(`${scoredDate}T00:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <h2 className="font-serif text-[18px] font-bold text-navy">Your day, mapped</h2>
        <span className="text-[11.5px] text-text-muted">{dateLabel}</span>
      </div>

      <div className="mb-4 grid grid-cols-4 gap-2 text-center">
        {[
          { label: isMtb ? "Descents" : "Runs", value: `${totals.runs}` },
          { label: "Distance", value: `${totals.skiKm} km` },
          { label: "Vertical", value: `${totals.verticalM} m` },
          { label: isMtb ? "Lift rides" : "Lifts", value: `${totals.liftRides}` },
        ].map((s) => (
          <div key={s.label} className="rounded-[10px] bg-warm-bg px-1 py-2">
            <div className="font-serif text-[17px] font-bold text-navy tabular-nums">{s.value}</div>
            <div className="text-[10px] uppercase tracking-[0.5px] text-text-muted">{s.label}</div>
          </div>
        ))}
      </div>

      {warnings.length > 0 ? (
        <div className="mb-4 space-y-1.5">
          {warnings.map((w, i) => (
            <p
              key={i}
              className="rounded-[10px] bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-800"
            >
              {w}
            </p>
          ))}
        </div>
      ) : null}

      <ol className="relative space-y-1">
        {groupItems(legs, sport).map((item, i) => {
          const wx = weatherByHour.get(Math.round(item.departHour));
          const role = item.kind === "stop" ? undefined : item.role;
          return (
            <li key={i} className="flex gap-3">
              <div className="flex w-[44px] shrink-0 flex-col items-end pt-2">
                <span className="text-[12px] font-semibold tabular-nums text-navy">
                  {formatHm(item.departHour)}
                </span>
              </div>
              <div
                className={`flex-1 rounded-[12px] border px-3.5 py-2.5 ${
                  item.kind === "stop"
                    ? "border-gold/30 bg-gold/[0.05]"
                    : "border-border-light bg-white"
                }`}
              >
                <div className="flex items-start gap-2">
                  <span aria-hidden className="text-[15px] leading-none">
                    {item.kind === "stop"
                      ? STOP_ICON[item.stopType]
                      : LEG_ICON[sport][item.kind === "descent" ? "trail" : item.kind]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[14px] font-semibold leading-tight text-navy">
                      <span className="min-w-0">{item.title}</span>
                      <RouteRoleBadge
                        role={role}
                        score={item.kind === "descent" && item.bestScore != null ? item.bestScore * 10 : undefined}
                        appeal={item.kind === "descent" ? item.appeal : undefined}
                        appealSource={item.kind === "descent" ? item.appealSource : undefined}
                        appealNote={item.kind === "descent" ? item.appealNote : undefined}
                      />
                      {item.kind === "lift" ? (
                        <span className="ml-1 text-[12px] font-normal text-text-muted">{item.direction === "down" ? "down" : "up"}</span>
                      ) : null}
                    </div>
                    {item.kind === "descent" ? (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-text-muted">
                        <span className="font-semibold text-gold">{item.bestScore == null ? "Forecast unavailable" : `Lowest section ${scoreOutOf100Label(item.bestScore)}`}</span>
                        {isMtb && item.difficulty ? (
                          <span>
                            <span aria-hidden style={{ color: difficultyColour(item.difficulty, "mtb") }}>
                              {difficultySymbol(item.difficulty, "mtb")}
                            </span>{" "}
                            Planning grade: {difficultyLabel(item.difficulty, "mtb")}
                            {item.sourceGrade ? ` · Source: ${item.sourceGrade}` : ""}
                          </span>
                        ) : null}
                        {item.aspect ? <span>{ASPECT_LABEL[item.aspect] ?? item.aspect}</span> : null}
                        {item.tempC != null && wx ? (
                          <span>
                            {Math.round(item.tempC)}°C at {wx.elevationM.toLocaleString("en-GB")} m
                          </span>
                        ) : null}
                        {wx ? <span>{wx.summary.toLowerCase()}</span> : null}
                        {!isMtb && item.groomed ? (
                          <span className="text-emerald-700">freshly groomed</span>
                        ) : null}
                      </div>
                    ) : null}
                    {item.kind === "lift" && item.suitability ? <p className="mt-1 text-xs text-text-muted">Expert ski ability required for this specialist lift. <a href={item.suitability.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-navy underline underline-offset-2">Ski Portillo guidance</a></p> : null}
                    {item.kind !== "stop" && item.reason ? (
                      <p className="mt-1 text-[12px] leading-snug text-text-muted">
                        {item.reason}
                      </p>
                    ) : null}
                    {item.kind === "stop" && item.detail ? (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-text-muted">
                        {item.detail.rating != null ? (
                          <span>★ {item.detail.rating.toFixed(1)}</span>
                        ) : null}
                        {item.detail.priceLevel != null ? (
                          <span>{priceText(item.detail.priceLevel)}</span>
                        ) : null}
                        {item.detail.cuisine ? <span>{item.detail.cuisine}</span> : null}
                        {item.detail.reservable ? <span className="text-emerald-700">bookable</span> : null}
                        {item.detail.mapsUri ? (
                          <a
                            href={item.detail.mapsUri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex min-h-[44px] items-center text-gold no-underline hover:underline"
                          >
                            {item.detail.source === "openstreetmap" ? "View on OpenStreetMap" : "View source"}
                          </a>
                        ) : null}
                        {item.detail.source === "openstreetmap" && <p className="basis-full">{item.detail.openingState === "open" ? "Listed hours cover this visit; opening is not confirmed live." : "Opening hours need checking."} {item.detail.accessMinutes ? `About ${item.detail.accessMinutes} minutes of local walking is included. ` : ""}Access is unverified. {placeEvidenceLabel(item.detail)}</p>}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {legs.some((l) => l.kind === "stop" && l.place) ? (
        <p className="mt-3 text-[11px] text-text-muted">Place data © OpenStreetMap contributors, ODbL. Source links and checked dates accompany mapped venues.</p>
      ) : null}
    </div>
  );
}
